// HouseholdSwitcher — header dropdown for switching between households
// - Shows the active household name with a ChevronDown icon (only when user has 2+ households)
// - Clicking opens a dropdown listing all households with role badges
// - Selecting a different household calls switchHousehold() and closes the dropdown
// - Closes on outside click or Escape key

import { Check, ChevronDown, Home, ShieldCheck, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useHouseholds } from "../hooks/useHouseholds";
import { useUser } from "../hooks/useUser";

export default function HouseholdSwitcher() {
  const { householdId } = useUser();
  const { households, switchHousehold } = useHouseholds();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const activeHousehold =
    households.find((h) => String(h.id) === householdId) ?? households[0];
  const hasMultiple = households.length > 1;

  if (!activeHousehold) {
    return null;
  }

  if (!hasMultiple) {
    // Single household — no switcher needed
    return (
      <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
        <Home className="h-3 w-3" aria-hidden="true" />
        <span>{activeHousehold.name}</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="mt-0.5 flex items-center gap-1 rounded-md text-xs text-gray-600 dark:text-gray-400"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Active household: ${activeHousehold.name}. Click to switch.`}
      >
        <Home className="h-3 w-3" aria-hidden="true" />
        <span className="max-w-40 truncate font-medium">
          {activeHousehold.name}
        </span>
        <ChevronDown
          className={`h-3 w-3 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Select household"
          className="absolute top-full left-0 z-50 mt-2 flex w-64 flex-col gap-1 rounded-lg border border-gray-300 bg-gray-100 p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          {households.map((h) => {
            const isActive = String(h.id) === householdId;
            return (
              <button
                key={h.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={async () => {
                  if (!isActive) {
                    await switchHousehold(String(h.id));
                  }
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  isActive
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-blue-900"
                }`}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">{h.name}</span>
                  <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    {h.role === "admin" ? (
                      <>
                        <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                        Admin
                      </>
                    ) : (
                      <>
                        <Users className="h-3 w-3" aria-hidden="true" />
                        Member
                      </>
                    )}
                  </span>
                </span>
                {isActive && (
                  <Check
                    className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
